import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search as SearchIcon, Zap, Code2, ChefHat, Hammer, Clapperboard, Wrench, Megaphone, GraduationCap, Bot, Users, Sparkles, Languages, ShieldCheck, Briefcase } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { ProfileCard, EntityCard, useMeta } from "../components/bits";

const HERO = [
  ["https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=700&q=70", "Electrician at work"],
  ["https://images.unsplash.com/photo-1574966740637-12c84035a4f2?w=700&q=70", "Chef cooking"],
  ["https://images.unsplash.com/photo-1614741118887-7a4ee193a5fa?w=700&q=70", "Developer coding"],
  ["https://images.unsplash.com/photo-1659353588580-8da374e328a1?w=700&q=70", "Carpenter crafting"],
];
const CATS = [["Electrical Wiring", Zap], ["React", Code2], ["Cooking", ChefHat], ["Carpentry", Hammer], ["Video Editing", Clapperboard], ["Plumbing", Wrench], ["Digital Marketing", Megaphone], ["Teaching", GraduationCap], ["Workflow Automation", Bot]];
const FAQ = [
  ["Do I need a degree to join?", "No. PrideFocus is skills-first. Degrees and certificates are optional. You can showcase your skills, work samples and contributions from day one."],
  ["Is PrideFocus free for members?", "Yes. Creating and sharing a skills profile is free."],
  ["Who can see my profile?", "You choose: public, members-only, or private. You also control who sees your location, credentials and each portfolio item, and who can contact or message you."],
  ["What does 'Credential verified' mean?", "Only that a PrideFocus administrator reviewed the supporting document or link you supplied. Self-declared skills and uploaded evidence are clearly labelled differently."],
  ["What is a My Pride Group?", "A team of members with complementary skills who offer services together. A group is not a legal business entity, and members always agree terms individually."],
  ["Is AI matching used to rank me?", "No. Search uses transparent keyword and filter matching. We do not compute any hidden 'worth' score."],
];

export default function Landing() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [feat, setFeat] = useState({ profiles: [], groups: [], communities: [] });
  useMeta(null, { noindex: false, description: "Build your digital skills résumé, showcase your work and get discovered. No degree required." });
  useEffect(() => { api.get("/featured").then((r) => setFeat(r.data)).catch(() => {}); }, []);
  const go = (e) => { e.preventDefault(); nav(`/search?q=${encodeURIComponent(q)}`); };
  return (
    <div>
      <section className="relative overflow-hidden bg-slate-900 text-white grain">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-orange-500/20 blur-3xl" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 fade-up">
            <p className="eyebrow !text-orange-400">{t("tagline")}</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]" data-testid="hero-title">{t("hero.title")}</h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">{t("hero.sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-accent text-base px-7 py-3.5" data-testid="hero-create-profile-btn">{t("nav.join")}</Link>
              <Link to="/search" className="btn text-base px-7 py-3.5 bg-white/10 hover:bg-white/20 text-white border border-white/20" data-testid="hero-discover-btn">{t("hero.cta2")}</Link>
            </div>
            <form onSubmit={go} className="mt-10 flex items-center bg-white rounded-full p-1.5 max-w-xl shadow-2xl" role="search">
              <SearchIcon className="ml-3 text-slate-400" size={20} aria-hidden />
              <label htmlFor="hero-search" className="sr-only">{t("search.placeholder")}</label>
              <input id="hero-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search.placeholder")} className="flex-1 px-3 py-2.5 text-slate-900 bg-transparent focus:outline-none" data-testid="hero-search-input" />
              <button className="btn-primary" data-testid="hero-search-btn">{t("search.button")}</button>
            </form>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {HERO.map(([src, alt], i) => (
              <div key={src} className={`self-start rounded-3xl overflow-hidden border-4 border-white/10 fade-up ${i % 2 ? "mt-10" : ""}`} style={{ animationDelay: `${i * 120}ms` }}>
                <img src={src} alt={alt} className="w-full h-44 sm:h-56 object-cover" loading={i > 1 ? "lazy" : "eager"} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="h2">{t("home.categories")}</h2>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {CATS.map(([name, Icon]) => (
            <Link key={name} to={`/search?skill=${encodeURIComponent(name)}`} className="card-hover p-4 flex items-center gap-3" data-testid={`category-${name}`}>
              <span className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 grid place-items-center"><Icon size={20} /></span><span className="font-medium text-sm">{name}</span>
            </Link>
          ))}
          <Link to="/search" className="card-hover p-4 flex items-center justify-center font-semibold text-sm text-orange-600">All skills →</Link>
        </div>
      </section>

      {feat.profiles.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-end justify-between"><h2 className="h2">{t("home.featured")}</h2><span className="text-xs muted">Profiles marked "Demo data" are fictional samples.</span></div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="featured-profiles">{feat.profiles.slice(0, 8).map((p) => <ProfileCard key={p.username} p={p} />)}</div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="h2">{t("home.how")}</h2>
        <div className="mt-10 grid md:grid-cols-4 gap-6">
          {["home.step1", "home.step2", "home.step3", "home.step4"].map((k, i) => (
            <div key={k} className="relative pl-14"><span className="absolute left-0 top-0 w-10 h-10 rounded-full bg-slate-900 text-orange-400 grid place-items-center font-display font-bold">{i + 1}</span><p className="font-semibold">{t(k)}</p></div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-2 gap-6">
        <div className="card p-8 lg:p-12 bg-gradient-to-br from-orange-50 to-white">
          <Sparkles className="text-orange-500" /><h2 className="h2 mt-4">{t("home.skillsfirst")}</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("home.skillsfirst_body")}</p>
          <div className="mt-6 flex flex-wrap gap-2"><span className="chip"><ShieldCheck size={12} /> Honest trust labels</span><span className="chip">No hidden scores</span><span className="chip">Degrees optional</span></div>
        </div>
        <div className="card p-8 lg:p-12">
          <h2 className="h2">{t("home.evidence")}</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("home.evidence_body")}</p>
          <div className="mt-6 grid grid-cols-3 gap-2">{HERO.slice(0, 3).map(([s, a]) => <img key={s} src={s} alt={a} loading="lazy" className="rounded-xl h-24 w-full object-cover" />)}</div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between"><h2 className="h2">{t("home.groups")}</h2><Link to="/groups" className="text-sm font-semibold text-orange-600">{t("nav.groups")} →</Link></div>
        <p className="mt-3 text-slate-600 max-w-2xl">{t("home.teams_body")}</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{feat.groups.map((g) => <EntityCard key={g.slug} e={g} base="/g/" />)}</div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between"><h2 className="h2">{t("home.communities")}</h2><Link to="/communities" className="text-sm font-semibold text-orange-600">{t("nav.communities")} →</Link></div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{feat.communities.map((c) => <EntityCard key={c.slug} e={c} base="/c/" />)}</div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-slate-900 text-white p-8 lg:p-12">
          <Briefcase className="text-orange-400" /><h2 className="text-2xl sm:text-3xl font-bold mt-4">{t("home.employers")}</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">{t("home.employers_body")}</p>
          <Link to="/register?type=employer" className="btn-accent mt-6" data-testid="employer-cta-btn">Create an employer account</Link>
        </div>
        <div className="card p-8 lg:p-12">
          <Languages className="text-orange-500" /><h2 className="h2 mt-4">{t("home.multilingual")}</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{t("home.multilingual_body")}</p>
          <p className="mt-4 text-sm font-medium">English · தமிழ் · हिन्दी · తెలుగు · ಕನ್ನಡ · മലയാളം · Español · Русский · 简体中文</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-10">
        <div><h2 className="h2">{t("home.testimonials")}</h2><p className="mt-3 muted text-sm" data-testid="testimonials-empty">{t("home.testimonials_empty")}</p>
          <div className="mt-6 card p-6"><Users className="text-orange-500" /><p className="mt-3 text-sm text-slate-600">Individuals and teams both belong here: build your own profile, then join or found a My Pride Group to showcase combined capabilities.</p></div></div>
        <div className="lg:col-span-2"><h2 className="h2">{t("home.faq")}</h2>
          <Accordion type="single" collapsible className="mt-6">{FAQ.map(([q2, a], i) => (
            <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left font-semibold" data-testid={`faq-${i}`}>{q2}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>))}
          </Accordion></div>
      </section>
    </div>
  );
}
